import React from 'react';
import { pdf, DocumentProps } from '@react-pdf/renderer';
import JSZip from 'jszip';
import { CombinedReport } from './CombinedReport';
import { fetchCombinedReportData } from './fetchCombinedReportData';
import { CombinedEntityType } from './combinedTypes';
import { ReportSortOption } from './combinedSort';
import { saveBlob } from '../saveBlob';

const triggerDownload = async (blob: Blob, filename: string): Promise<void> => {
  await saveBlob(blob, filename);
};

const sanitize = (roid: string, fallback: string): string =>
  (roid || fallback).replace(/[^\w.-]+/g, '_');

async function buildBlob(
  entityId: string,
  entityType: CombinedEntityType,
  cloudName: string,
  sort?: ReportSortOption
): Promise<{ blob: Blob; roid: string } | null> {
  const data = await fetchCombinedReportData({
    entityId,
    entityType,
    cloudName,
    sort,
  });
  if (!data) return null;
  const element = React.createElement(CombinedReport, {
    data,
  }) as React.ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();
  return { blob, roid: data.parent.roid };
}

/**
 * Genera el informe combinado "con todo".
 * - 1 orden  → descarga un único PDF.
 * - N órdenes → descarga un ZIP con un PDF por orden.
 */
export async function generateCombinedPDF(params: {
  entityIds: string[];
  entityType: CombinedEntityType;
  cloudName: string;
  /** Orden de las entidades internas elegido en el modal. */
  sort?: ReportSortOption;
  onProgress?: (current: number, total: number) => void;
}): Promise<void> {
  const { entityIds, entityType, cloudName, sort, onProgress } = params;
  if (entityIds.length === 0) return;

  if (entityIds.length === 1) {
    const res = await buildBlob(entityIds[0], entityType, cloudName, sort);
    if (res) {
      triggerDownload(res.blob, `informe-${sanitize(res.roid, entityIds[0])}.pdf`);
    }
    return;
  }

  const zip = new JSZip();
  let done = 0;
  for (const id of entityIds) {
    const res = await buildBlob(id, entityType, cloudName, sort);
    if (res) {
      zip.file(`informe-${sanitize(res.roid, id)}.pdf`, res.blob);
    }
    done++;
    onProgress?.(done, entityIds.length);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const stamp = new Date().toISOString().slice(0, 10);
  triggerDownload(zipBlob, `informes-${entityType}-${stamp}.zip`);
}
